import { Box, IconButton, Tooltip, Typography } from "@mui/material";
import { TimeSince } from "components/DateTime/TimeSince";
import { FlexBox } from "components/Wrappers/FlexBox/FlexBox";
import { LinkWrapper } from "components/Link/LinkWrapper";
import { ImagePreview } from "components/Image/ImagePreview";
import { ProductContextMenu } from "components/Menu/ProductContextMenu";
import { CONTEXT_MENU_LABELS } from "../constants/product.constants";
import { HiddenContent } from "components/Text/HiddenContent";
import RecommendIcon from "@mui/icons-material/Recommend";
import dayjs from "configs/dayjs.config";

import { dateTimeFormat } from "constants/componentsConstants";
import TaskIcon from "@mui/icons-material/Task";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";

export const productListColumnsL = ({
  handlePreviewClick,
  handleUpdateProducts,
  handleDeleteClick,
  handleCopyChecklist,
  handleCopyTimeline,
  isBdc,
  isUnboundMode,
  handleUpdateProductExecutionTimeline,
  handleUpdateProductHandoverChecklist,
  handleApprovePMCClick,
  pageAccess,
  handleInputOutputButtonClick,
  handleHandoverChecklistButtonClick,
  handleTimelineButtonClick,
}) => {
  const columns = [
    {
      id: "select",
      header: ({ table }) => (
        <input
          type="checkbox"
          checked={table.getIsAllPageRowsSelected()}
          onChange={table.getToggleAllPageRowsSelectedHandler()}
        />
      ),
      cell: ({ row }) => (
        <input
          type="checkbox"
          checked={row.getIsSelected()}
          disabled={!row.getCanSelect()}
          onChange={row.getToggleSelectedHandler()}
        />
      ),
    },
    {
      accessorKey: "refImage",
      header: "RefImage",
      cell: ({ row }) => <ImagePreview src={row.original.refImage} />,
    },
    {
      accessorKey: "productName",
      header: "Product Name",
      cell: ({ row }) => (
        <FlexBox justifyContent="space-between">
          <LinkWrapper
            onClick={() => handlePreviewClick(row.original)}
            color="textPrimary"
          >
            {row.original.partLotDocCode}
            <br />
            {row.original.productName}
            <br />
            <b>Lot</b> - {row.original.lotPartCode} /{" "}
            {row.original.lotPartCount}
            {row.original.productUom}
            <br />
            {row.original.productParentCategory}-
            {row.original.childParentCategory}
          </LinkWrapper>
        </FlexBox>
      ),
    },
    {
      accessorKey: "salesPrice",
      header: "Rate",
      cell: ({ row }) => (
        <Box>
          <Box>
            {row.original.lotPartCount} of {row.original.productQuantity}
            {row.original.productUom} @ {row.original.sellingPrice}
          </Box>
          {isBdc && <HiddenContent>CP-{row.original.costPrice}</HiddenContent>}
        </Box>
      ),
    },
    {
      accessorKey: "lotPartDeadline",
      header: "Deadline",
      cell: ({ row }) => (
        <TimeSince startDate={row.original.lotPartDeadline} variant="data" />
      ),
    },
    {
      accessorKey: "pmc",
      header: "PMC",
      cell: ({ row }) =>
        row.original.pmc && (
          <Box>
            <FlexBox justifyContent="space-between">
              <Box>
                {row.original.pmc}
                <br />
                <TimeSince startDate={row.original.pmcAssignDate} />
              </Box>
              <IconButton
                onClick={() =>
                  pageAccess.canApproval &&
                  handleApprovePMCClick(
                    row.original,
                    Number(!row.original.isPmcApproved)
                  )
                }
              >
                <Tooltip
                  title={`${row.original.pmcApprovalBy} on ${
                    row.original.pmcApprovalDate
                      ? dayjs(row.original.pmcApprovalDate).format(
                          "DD-MMM-YYYY"
                        )
                      : "Pending"
                  }`}
                >
                  {row.original.isPmcApproved ? (
                    <RecommendIcon />
                  ) : (
                    <PriorityHighIcon />
                  )}
                </Tooltip>
              </IconButton>
            </FlexBox>
          </Box>
        ),
    },
    {
      accessorKey: "createdBy",
      header: "BDC",
      cell: ({ row }) => (
        <Box>
          {row.original.createdBy}
          <br />
          <TimeSince startDate={row.original.createdOn} />
        </Box>
      ),
    },
  ];
  if (isUnboundMode) {
    columns.push({
      accessorKey: "checklist",
      header: "Handover Checklist",
      cell: ({ row }) => (
        <LinkWrapper
          onClick={() => handleHandoverChecklistButtonClick(row.original)}
          color="textPrimary"
        >
          <FlexBox justifyContent="space-between" flex={1}>
            <Box>
              {row.original.checklistStatus.done}/
              {row.original.checklistStatus.total}
            </Box>
            <Box>
              {row.original.checklistStatus.checklistApprovalStatus && (
                <RecommendIcon />
              )}
            </Box>
          </FlexBox>
        </LinkWrapper>
      ),
    });
  }
  if (isUnboundMode) {
    columns.push({
      accessorKey: "executionTimeline",
      header: "Execution Timeline",
      cell: ({ row }) =>
        row.original.executionTimeline.startDate && (
          <LinkWrapper
            onClick={() => handleTimelineButtonClick(row.original)}
            color="textPrimary"
          >
            <FlexBox justifyContent="space-between" alignItems="center">
              <FlexBox flexDirection="column" alignItems="center">
                <Box>
                  {dayjs(row.original.executionTimeline.startDate).format(
                    dateTimeFormat.date
                  )}
                </Box>
                <Box>to</Box>
                <Box>
                  {dayjs(row.original.executionTimeline.endDate).format(
                    dateTimeFormat.date
                  )}
                </Box>
              </FlexBox>
              <Box>
                <Box>
                  {row.original.executionTimeline.timelineIsApproved && (
                    <RecommendIcon />
                  )}
                </Box>
                <Box>{row.original.task ? <TaskIcon /> : ""}</Box>
              </Box>
            </FlexBox>
            <Box padding="8px 16px">
              {dayjs(row.original.lotPartDeadline).diff(
                dayjs(row.original.executionTimeline.endDate),
                "day",
                true
              )
                ? dayjs(row.original.lotPartDeadline).diff(
                    dayjs(row.original.executionTimeline.endDate),
                    "day",
                    true
                  ) + " Day(s)"
                : ""}
            </Box>
          </LinkWrapper>
        ),
    });
  }
  if (isUnboundMode) {
    columns.push({
      accessorKey: "io",
      header: "Input/Output",
      cell: ({ row }) => (
        <LinkWrapper
          onClick={() => handleInputOutputButtonClick(row.original)}
          color="textPrimary"
        >
          <Typography>I/O</Typography>
        </LinkWrapper>
      ),
    });
  }
  if (isBdc) {
    columns.push({
      accessorKey: "action",
      header: "Action",
      cell: ({ row }) =>
        isBdc && (
          <ProductContextMenu
            handleCopyAction1={
              row.original.checklistStatus.done > 0
                ? () => handleCopyChecklist(row.original.lotPartId)
                : null
            }
            copyLabel1={CONTEXT_MENU_LABELS.COPY_HANDOVER_CHECKLIST}
            handleCopyAction2={
              row.original.executionTimeline?.startDate
                ? () => handleCopyTimeline(row.original.lotPartId)
                : null
            }
            copyLabel2={CONTEXT_MENU_LABELS.COPY_EXECUTION_TIMELINE}
            handleDeleteAction={() => handleDeleteClick(row.original)}
            deleteLabel={CONTEXT_MENU_LABELS.DELETE_ROW}
            handleEditAction={() => handleUpdateProducts(row.original)}
            editLabel={CONTEXT_MENU_LABELS.EDIT_ROW}
            handleEditExecutionTimeline={
              row.original.executionTimeline?.startDate
                ? () => handleUpdateProductExecutionTimeline(row.original)
                : null
            }
            handleEditExecutionTimelineLabel={
              CONTEXT_MENU_LABELS.EDIT_EXECUTION_TIMELINE
            }
            handleEditHandoverChecklistTimeline={
              row.original.checklistStatus.done > 0 &&
              !row.original.checklistStatus.checklistApprovalStatus
                ? () => handleUpdateProductHandoverChecklist(row.original)
                : null
            }
            handleEditHandoverChecklistTimelineLabel={
              CONTEXT_MENU_LABELS.EDIT_HANDOVER_CHECKLIST_TIMELINE
            }
          />
        ),
    });
  }
  return columns;
};
